import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Scissors, Palette, Shield, MessageCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface HistoryItem {
  request_id: string;
  question_text: string | null;
  service_type: string | null;
  created_at: string;
  payload: { result?: string; sources?: unknown } | null;
}

interface Props {
  onSelect: (item: HistoryItem) => void;
  onNewQuestion: () => void;
  refreshKey?: number;
}

function serviceIcon(s: string | null) {
  if (s === 'COLOR') return <Palette className="h-3.5 w-3.5" />;
  if (s === 'BOTH') return <Shield className="h-3.5 w-3.5" />;
  if (s === 'CUTTING') return <Scissors className="h-3.5 w-3.5" />;
  return <MessageCircle className="h-3.5 w-3.5" />;
}
function serviceLabel(s: string | null) {
  if (s === 'COLOR') return 'Color';
  if (s === 'BOTH') return 'Hair Rescue';
  if (s === 'CUTTING') return 'Haircut';
  return 'Question';
}

export function useAskHistory(refreshKey = 0) {
  const { user } = useAuth();
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) { setItems([]); return; }
    let cancelled = false;
    setLoading(true);
    supabase
      .from('analysis_cache')
      .select('request_id, question_text, service_type, created_at, payload')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(100)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error && data) setItems(data as unknown as HistoryItem[]);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [user, refreshKey]);

  return { items, loading };
}

function HistoryList({ items, loading, onSelect }: { items: HistoryItem[]; loading: boolean; onSelect: (i: HistoryItem) => void }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  if (!items.length) {
    return (
      <p className="px-4 py-6 text-center font-body text-xs text-muted-foreground">
        Your past questions will appear here.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-1 px-2 pb-4">
      {items.map((it) => {
        const title = (it.question_text || 'Photo consultation').trim();
        const first = title.split('\n')[0].slice(0, 80);
        const date = new Date(it.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        return (
          <button
            key={it.request_id}
            onClick={() => onSelect(it)}
            className="group w-full text-left rounded-xl border border-transparent px-3 py-2.5 hover:border-border hover:bg-accent/30 transition-colors"
          >
            <p className="font-body text-sm text-foreground line-clamp-2 leading-snug">{first || 'Photo consultation'}</p>
            <div className="mt-1 flex items-center gap-2 text-muted-foreground">
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium">
                {serviceIcon(it.service_type)}
                {serviceLabel(it.service_type)}
              </span>
              <span className="font-body text-[10px]">{date}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

export function AskHistorySidebar({ onSelect, onNewQuestion, refreshKey }: Props) {
  const { items, loading } = useAskHistory(refreshKey);
  return (
    <aside className="hidden md:flex flex-col w-72 shrink-0 border-r border-border bg-card/40 h-[100dvh]">
      <div className="flex items-center justify-between px-4 pt-5 pb-3 border-b border-border">
        <div>
          <p className="font-display text-sm text-foreground">Your Questions</p>
          <p className="font-body text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">History</p>
        </div>
        <button
          onClick={onNewQuestion}
          className="inline-flex items-center gap-1.5 rounded-lg bg-foreground px-2.5 py-1.5 font-body text-[11px] font-semibold text-primary-foreground hover:opacity-90"
        >
          <Plus className="h-3 w-3" />
          New
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        <HistoryList items={items} loading={loading} onSelect={onSelect} />
      </div>
    </aside>
  );
}

export function AskHistoryDrawer({
  open, onClose, onSelect, onNewQuestion, refreshKey,
}: Props & { open: boolean; onClose: () => void }) {
  const { items, loading } = useAskHistory(refreshKey);
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-foreground/40 backdrop-blur-sm md:hidden"
            onClick={onClose}
          />
          <motion.aside
            initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            className="fixed left-0 top-0 z-[70] flex h-[100dvh] w-80 max-w-[85vw] flex-col border-r border-border bg-background md:hidden"
          >
            <div className="flex items-center justify-between px-4 pt-5 pb-3 border-b border-border">
              <div>
                <p className="font-display text-sm text-foreground">Your Questions</p>
                <p className="font-body text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">History</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { onNewQuestion(); onClose(); }}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-foreground px-2.5 py-1.5 font-body text-[11px] font-semibold text-primary-foreground"
                >
                  <Plus className="h-3 w-3" /> New
                </button>
                <button onClick={onClose} className="rounded-full p-1.5 text-muted-foreground hover:bg-muted">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              <HistoryList items={items} loading={loading} onSelect={(i) => { onSelect(i); onClose(); }} />
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
